import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeCabDeck, ChangeCabDeckTheme } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  DEFAULT_CAB_DECK_THEME,
  type CabDeckTheme,
} from '../cabDeckTheme';
import '../cabPresentationTheme.css';

function ThemeRange({
  label,
  value,
  defaultValue,
  min,
  max,
  step,
  disabled,
  onChange,
  format = (n) => String(n),
}: {
  label: string;
  value: number;
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (value: number) => void;
  format?: (value: number) => string;
}) {
  const pct = ((defaultValue - min) / (max - min)) * 100;
  return (
    <div className="cab-theme-range mb-2">
      <Form.Label className="small mb-0">
        {label}: <strong>{format(value)}</strong>
      </Form.Label>
      <div className="position-relative">
        <span className="cab-theme-range-track-mark" style={{ left: `${pct}%` }} title={`Padrão ${format(defaultValue)}`} />
        <Form.Range
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </div>
      <div className="cab-theme-range-legend">
        <span>{format(min)}</span>
        <span>padrão {format(defaultValue)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

const ChangeCabSlidesPanel: React.FC = () => {
  const { user } = useAuth();
  const canManage = isChangeManager(user);
  const [deck, setDeck] = useState<ChangeCabDeck | null>(null);
  const [theme, setTheme] = useState<CabDeckTheme>({ ...DEFAULT_CAB_DECK_THEME });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await changesApi.getGlobalCabDeck();
      setDeck(next);
      setTheme({ ...DEFAULT_CAB_DECK_THEME, ...(next.theme as CabDeckTheme) });
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar o modelo global da apresentação.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveTheme = async () => {
    try {
      const next = await changesApi.updateGlobalCabDeckTheme(theme as Partial<ChangeCabDeckTheme>);
      setDeck(next);
      setSaved(true);
      setError(null);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar o tema.'));
    }
  };

  if (loading) {
    return <p className="text-muted mb-0">Carregando modelo global…</p>;
  }

  return (
    <div>
      <p className="text-muted">
        Esta aba configura o <strong>modelo global</strong> da apresentação CAB (tema + páginas
        padrão). A edição visual (títulos, campos, imagens, arrastar blocos, inserir slides) fica no
        editor em tela cheia. Cada mudança pode ter a própria cópia a partir da ficha.
      </p>
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <div className="border rounded p-3 mb-3 bg-white">
        <h6 className="mb-2">Tema global do deck</h6>
        <div className="row g-2">
          <div className="col-md-3">
            <Form.Label className="small mb-0">Fundo da capa</Form.Label>
            <Form.Control
              type="color"
              value={theme.bg}
              disabled={!canManage}
              onChange={(e) => setTheme((prev) => ({ ...prev, bg: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <Form.Label className="small mb-0">Azul (títulos)</Form.Label>
            <Form.Control
              type="color"
              value={theme.blue}
              disabled={!canManage}
              onChange={(e) => setTheme((prev) => ({ ...prev, blue: e.target.value, text: e.target.value }))}
            />
          </div>
          <div className="col-md-3">
            <Form.Label className="small mb-0">Verde</Form.Label>
            <Form.Control
              type="color"
              value={theme.green}
              disabled={!canManage}
              onChange={(e) =>
                setTheme((prev) => ({ ...prev, green: e.target.value, chromeAccent: e.target.value }))
              }
            />
          </div>
          <div className="col-md-3">
            <Form.Label className="small mb-0">Fundo do cronômetro</Form.Label>
            <Form.Control
              type="color"
              value={theme.chromeBg}
              disabled={!canManage}
              onChange={(e) => setTheme((prev) => ({ ...prev, chromeBg: e.target.value }))}
            />
          </div>
          <div className="col-md-6">
            <Form.Label className="small mb-0">Fonte do título</Form.Label>
            <Form.Select
              value={theme.titleFontFamily}
              disabled={!canManage}
              onChange={(e) => setTheme((prev) => ({ ...prev, titleFontFamily: e.target.value }))}
            >
              <option value='"Segoe UI", Arial, Helvetica, sans-serif'>Segoe UI / Arial</option>
              <option value="Arial, Helvetica, sans-serif">Arial</option>
              <option value="Georgia, serif">Georgia</option>
              <option value='"Trebuchet MS", sans-serif'>Trebuchet</option>
              <option value="Verdana, sans-serif">Verdana</option>
            </Form.Select>
          </div>
          <div className="col-md-6">
            <ThemeRange
              label="Tamanho do título (vw)"
              value={theme.titleFontSizeVw}
              defaultValue={DEFAULT_CAB_DECK_THEME.titleFontSizeVw}
              min={2}
              max={4.5}
              step={0.1}
              disabled={!canManage}
              onChange={(titleFontSizeVw) => setTheme((prev) => ({ ...prev, titleFontSizeVw }))}
              format={(n) => n.toFixed(1)}
            />
            <ThemeRange
              label="Peso do título"
              value={theme.titleFontWeight}
              defaultValue={DEFAULT_CAB_DECK_THEME.titleFontWeight}
              min={400}
              max={800}
              step={100}
              disabled={!canManage}
              onChange={(titleFontWeight) => setTheme((prev) => ({ ...prev, titleFontWeight }))}
            />
          </div>
        </div>
        {canManage ? (
          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button size="sm" onClick={() => void saveTheme()}>
              Salvar tema
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setTheme({ ...DEFAULT_CAB_DECK_THEME })}
            >
              Restaurar padrão CCO
            </Button>
            {saved ? <span className="small text-success align-self-center">Tema salvo.</span> : null}
          </div>
        ) : (
          <p className="small text-muted mb-0 mt-2">Somente admin/analista altera o tema.</p>
        )}
      </div>

      <div className="border rounded p-3 bg-white">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <h6 className="mb-0">Páginas do modelo ({deck?.slides?.length || 0})</h6>
          <Link className="btn btn-primary btn-sm" to="/changes/cab-deck/global">
            Abrir editor do modelo
          </Link>
        </div>
        <ul className="mb-0">
          {(deck?.slides || []).map((slide, index) => (
            <li key={slide.id || index}>
              <strong>{slide.title}</strong>
              <span className="text-muted"> — {slide.elements?.length || 0} elementos</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ChangeCabSlidesPanel;
